import { memo, useEffect, useRef } from 'react';
import type { Sight } from '../../types';
import type { SplatSceneApi } from '../../scene/splatSceneApi';
import { createSdkScene } from '../../scene/sdk/createSdkScene';
import { detectQualityProfileAsync } from '../../scene/qualityProfile';
import type { SceneConfig } from '../../scene/sceneConfig';
import { $appConfig } from '../../state/appConfig';
import { $activeScene } from '../../state/sceneNavigation';
import { $sceneStateSnapshots } from '../../state/sceneState';
import { identifyDevice, identifyRenderEngine } from '../../analytics/deviceTracker';
import { logEvent as logMemoryEvent, trackPostDispose } from '../../debug/memoryTracker';
import styles from './SceneCanvas.module.css';

interface SceneCanvasProps {
  sights: Sight[];
  config: SceneConfig;
  onApiReady?: (api: SplatSceneApi | null) => void;
}

const SceneCanvasComponent = ({ sights, config, onApiReady }: SceneCanvasProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sdkHostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    let disposed = false;
    let api: SplatSceneApi | null = null;

    detectQualityProfileAsync().then((qualityProfile) => {
      if (disposed || !containerRef.current) return;

      identifyDevice(qualityProfile);

      // Scene-state restore: when the user returns from an inside-scene
      // (e.g. `kaaba-inside → kaaba`), the snapshot for the parent's
      // `poiFilterId` is present. We bypass the entire preloader
      // cinematic (preview-tour + flyover + reveal shader) and place the
      // camera exactly where it was saved. The 300ms minimum
      // particle-sphere display window lives on the App side — keeps the
      // visual handoff clean even on warm HTTP cache.
      //
      // Ключ snapshot — `poiFilterId` (= `setSceneId` payload), а НЕ
      // `config.id` (для main-сцен это URL-id вроде `'kaaba'`, а
      // `poiFilterId` — `'main'`; для inside-сцен они совпадают).
      // Без этого `SceneCanvas` промахивался при возврате из kaaba-inside
      // и `skipAnimations` оставался false — preloader/flyover играл,
      // несмотря на восстановленный чат.
      const activeScene = $activeScene.getState();
      const snapshotKey = activeScene?.poiFilterId ?? config.id;
      const snapshot = $sceneStateSnapshots.getState().get(snapshotKey);
      const devSkip = $appConfig.getState().dev.skipPreloaderAnimation;

      // The engine mounts into a FULL-VIEWPORT host (clipped by `.canvasRoot`,
      // overflow:hidden) so the chat panel reframes via the camera view-offset
      // WITHOUT resizing the canvas.
      const sceneContainer = sdkHostRef.current;
      if (!sceneContainer) return;
      identifyRenderEngine();
      api = createSdkScene({
        container: sceneContainer,
        sights,
        config,
        qualityProfile,
        // Dev flag — read once at mount, so a toggle applies from the next
        // scene entry. OR: skip when restoring from a snapshot — preloader
        // cinematic makes no sense for a scene the user has just left.
        skipAnimations: devSkip || snapshot != null,
        initialCameraPose: snapshot?.cameraPose,
      });
      onApiReady?.(api);
    });

    return () => {
      disposed = true;
      onApiReady?.(null);
      const disposeStart = performance.now();
      logMemoryEvent('SceneCanvas cleanup start', {
        sceneId: config.id,
        hasApi: !!api,
      });
      api?.dispose();
      // Probe whether the SplatSceneApi object itself (which closes over the
      // whole scene closure, engine included) gets GC'd after we drop our
      // refs. If it stays alive at T+9s, something downstream still holds it.
      if (api) trackPostDispose('splatApi', api, { sceneId: config.id });
      // Drop our local strong reference so the WeakRef has a chance to GC.
      api = null;
      logMemoryEvent('SceneCanvas cleanup done', {
        sceneId: config.id,
        durationMs: +(performance.now() - disposeStart).toFixed(1),
      });
    };
  }, [sights, config]);

  return (
    <div ref={containerRef} className={styles.canvasRoot}>
      <div ref={sdkHostRef} className={styles.sdkHost} />
    </div>
  );
};

export const SceneCanvas = memo(SceneCanvasComponent);
SceneCanvas.displayName = 'SceneCanvas';
